/**
 * 文件选择 Hook
 *
 * 提供浏览器原生文件选择能力，通过动态创建 input[type=file] 实现。
 * 与 useFileUpload（管理上传过程）互补，本 hook 仅负责文件选择和读取。
 *
 * 提供文件选择、验证和预览等功能
 */

import { useCallback, useRef } from 'react';

/**
 * 文件选择选项
 */
export interface FileSelectOptions {
  /** 接受的文件类型，如 '.json', '.csv', 'image/*' */
  accept?: string;
  /** 是否允许多选 */
  multiple?: boolean;
}

/**
 * useFileSelector 返回值
 */
export interface UseFileSelectorReturn {
  /** 选择并读取 JSON 文件，返回解析后的数据 */
  selectJSONFile: (options?: FileSelectOptions) => Promise<unknown>;
  /** 选择文件，返回 File 对象 */
  selectFile: (options?: FileSelectOptions) => Promise<File>;
  /** 选择多个文件，返回 File 数组 */
  selectFiles: (
    options?: Omit<FileSelectOptions, 'multiple'>,
  ) => Promise<File[]>;
}

/**
 * 创建隐藏的 file input 元素，统一处理并发防护和清理逻辑
 *
 * @param pendingRef - 并发防护 ref
 * @param accept - 文件类型过滤
 * @param multiple - 是否允许多选
 * @param onFiles - 文件选中后的回调
 */
function openFileDialog<T>(
  pendingRef: React.MutableRefObject<boolean>,
  accept: string,
  multiple: boolean,
  onFiles: (files: FileList) => T | Promise<T>,
): Promise<T> {
  if (pendingRef.current) {
    return Promise.reject(new Error('File selection in progress'));
  }
  pendingRef.current = true;

  return new Promise<T>((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    input.accept = accept;
    input.multiple = multiple;
    document.body.appendChild(input);

    let settled = false;

    const settle = () => {
      if (!settled) {
        settled = true;
        pendingRef.current = false;
        // 清理 input 元素和事件
        input.onchange = null;
        input.removeEventListener('cancel', handleCancel);
        if (document.body.contains(input)) {
          document.body.removeChild(input);
        }
      }
    };

    const handleCancel = () => {
      if (!settled) {
        settle();
        reject(new Error('Selection cancelled'));
      }
    };

    input.addEventListener('cancel', handleCancel);

    input.onchange = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      settle();

      if (!files || files.length === 0) {
        reject(new Error('No file selected'));
        return;
      }

      try {
        const result = await onFiles(files);
        resolve(result);
      } catch (err) {
        reject(err);
      }
    };

    input.click();
  });
}

/**
 * 文件选择 Hook
 *
 * 通过动态创建隐藏的 input[type=file] 元素来选择文件。
 * 内置并发防护，同一时间只允许一个文件选择操作。
 *
 * @example
 * ```tsx
 * const { selectJSONFile, selectFile } = useFileSelector();
 *
 * // 选择 JSON 文件
 * const data = await selectJSONFile();
 *
 * // 选择任意文件
 * const file = await selectFile({ accept: '.csv,.xlsx' });
 * ```
 */
export function useFileSelector(): UseFileSelectorReturn {
  // 并发防护：同一时间只允许一个文件选择操作
  const pendingRef = useRef(false);

  const selectJSONFile = useCallback(
    (options?: FileSelectOptions): Promise<unknown> =>
      openFileDialog(
        pendingRef,
        options?.accept || '.json',
        false,
        async (files) => {
          const file = files[0];
          const text = await file.text();
          try {
            return JSON.parse(text);
          } catch {
            throw new Error('Invalid JSON file');
          }
        },
      ),
    [],
  );

  const selectFile = useCallback(
    (options?: FileSelectOptions): Promise<File> =>
      openFileDialog(
        pendingRef,
        options?.accept || '*',
        false,
        (files) => files[0],
      ),
    [],
  );

  const selectFiles = useCallback(
    (options?: Omit<FileSelectOptions, 'multiple'>): Promise<File[]> =>
      openFileDialog(pendingRef, options?.accept || '*', true, (files) =>
        Array.from(files),
      ),
    [],
  );

  return { selectJSONFile, selectFile, selectFiles };
}
